/**
 * The login channel on every request after sign-in (HS-1615).
 *
 * Admin and Portal call the same API host, and the browser keeps one cookie jar per host. The API
 * therefore keeps one session cookie pair per channel (`accessToken_admin`, `accessToken_portal`,
 * …) and picks the pair from the `X-Auth-Channel` request header. Without the header it falls back
 * to the legacy names, which is what the device still uses; with it there is no fallback.
 *
 * The header value is `LOGIN_CHANNEL[app]` on purpose: the login request sends the same value in
 * its body, and the API refuses a login whose header and `loginChannel` differ
 * (`400 AUTH_CHANNEL_MISMATCH`). One source keeps the two from drifting apart.
 */

import { LOGIN_CHANNEL, type AuthApp } from './client';

/** The request header the API reads the login channel from. */
export const AUTH_CHANNEL_HEADER = 'X-Auth-Channel';

/**
 * The query parameter that carries the channel where a header cannot be sent.
 *
 * A `<video src>` request sends cookies but no custom header, so the two certification video
 * stream routes also accept the channel from the query string. Every other route ignores it.
 */
export const AUTH_CHANNEL_QUERY = 'authChannel';

/** Default headers for every API request an app makes. Passed to `configureApiClient`. */
export function authChannelHeaders(app: AuthApp): Record<string, string> {
  return { [AUTH_CHANNEL_HEADER]: LOGIN_CHANNEL[app] };
}

/**
 * Add `authChannel=<channel>` to a URL that a media element loads directly.
 *
 * Works on absolute and relative URLs alike (Admin dev serves the API under a relative `/api`), so
 * the URL is split by hand rather than parsed with `URL`, which needs a base for the relative form.
 * An existing query string and `#fragment` are kept; an existing `authChannel` is replaced, not
 * repeated.
 */
export function withAuthChannel(url: string, app: AuthApp): string {
  const hashAt = url.indexOf('#');
  const fragment = hashAt === -1 ? '' : url.slice(hashAt);
  const beforeFragment = hashAt === -1 ? url : url.slice(0, hashAt);

  const queryAt = beforeFragment.indexOf('?');
  const path = queryAt === -1 ? beforeFragment : beforeFragment.slice(0, queryAt);
  const params = new URLSearchParams(queryAt === -1 ? '' : beforeFragment.slice(queryAt + 1));
  params.set(AUTH_CHANNEL_QUERY, LOGIN_CHANNEL[app]);

  return `${path}?${params.toString()}${fragment}`;
}
